import { useState } from 'react'

import { formatDate, monogramColors, timeAgo } from '../../../../format'
import { askQuestion } from '../../../../api'

import style from './desk.module.css'

/**
 * The staffed desk, and the fit check.
 *
 * Two things a careers page cannot do, side by side.
 *
 * The desk answers "is anyone actually here?" — and answers it with evidence
 * rather than a badge. Every figure comes from questions the company has really
 * answered: who answered them, how many, how recently, how fast. A "monitored by
 * a human" label would be worth nothing, because anyone can set one.
 *
 * The quiz is the company's own two-minute fit check. It is the light version of
 * the sprint's day-one assessment — same principle, judge the person by how they
 * think rather than by their CV. Nothing is stored: it exists so a candidate
 * learns something and self-selects, not so the company harvests answers.
 */

/**
 * A visible tooltip. Opens on hover AND on click, so it works on touch, and is
 * a real button so it is keyboard reachable — none of which the native `title`
 * attribute gives you.
 */
const Tip = ({ label, text, onDark = false }) => {
  const [open, setOpen] = useState(false)

  return (
    <span className={style.tip_wrap}>
      <button
        type="button"
        className={onDark ? `${style.tip_btn} ${style.tip_btn_dark}` : style.tip_btn}
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <span className={style.tip} role="tooltip">
          {text}
        </span>
      )}
    </span>
  )
}

const Quiz = ({ quiz, brand }) => {
  const [step, setStep] = useState(-1) // -1 = intro
  const [answers, setAnswers] = useState({})

  const total = quiz.questions.length
  const done = step >= total

  const score = Object.values(answers).reduce((sum, n) => sum + n, 0)
  const outcome =
    quiz.outcomes.find((o) => score >= o.min && score <= o.max) ||
    quiz.outcomes[quiz.outcomes.length - 1] ||
    null

  const choose = (questionId, optionScore) => {
    setAnswers((prev) => ({ ...prev, [questionId]: optionScore }))
    setStep((s) => s + 1)
  }

  const restart = () => {
    setAnswers({})
    setStep(-1)
  }

  if (step === -1) {
    return (
      <div className={style.quiz_intro}>
        <p className={style.quiz_title}>{quiz.title}</p>
        {quiz.intro && <p className={style.quiz_lede}>{quiz.intro}</p>}
        <button
          type="button"
          className={style.quiz_start}
          style={{ background: brand }}
          onClick={() => setStep(0)}
          data-log-action="spotlight_quiz_start"
        >
          Start · {total} questions
        </button>
      </div>
    )
  }

  if (done) {
    return (
      <div className={style.quiz_result}>
        <span className={style.quiz_score} style={{ color: brand }}>
          {outcome ? outcome.title : 'Done'}
        </span>
        {outcome && <p className={style.quiz_message}>{outcome.message}</p>}
        <button
          type="button"
          className={style.quiz_again}
          onClick={restart}
          data-log-action="spotlight_quiz_restart"
        >
          Take it again
        </button>
      </div>
    )
  }

  const q = quiz.questions[step]

  return (
    <div className={style.quiz_step}>
      <div className={style.quiz_progress} aria-hidden="true">
        {quiz.questions.map((item, i) => (
          <span
            key={item.id}
            className={i <= step ? `${style.pip} ${style.pip_on}` : style.pip}
            style={i <= step ? { background: brand } : undefined}
          />
        ))}
      </div>

      <p className={style.quiz_prompt}>{q.prompt}</p>

      <div className={style.quiz_options}>
        {q.options.map((o) => (
          <button
            key={o.id}
            type="button"
            className={style.quiz_option}
            onClick={() => choose(q.id, o.score)}
            data-log-action="spotlight_quiz_answer"
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

const BoothDesk = ({ company, host, quiz, badges = [], sprintId, onAuthError }) => {
  const brand = company.brandColor || '#7B5EA7'
  const colors = monogramColors(company.orgKey)

  const [draft, setDraft] = useState('')
  const [open, setOpen] = useState(false)
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const submit = async (event) => {
    event.preventDefault()
    const question = draft.trim()
    if (question.length < 5) return setError('A little more detail, please.')
    setBusy(true)
    setError(null)
    try {
      await askQuestion({ orgKey: company.orgKey, sprintId, question })
      setSent(true)
      setDraft('')
    } catch (err) {
      if (!(onAuthError && onAuthError(err))) setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (!host && !quiz) return null

  return (
    <div className={style.desk}>
      {host && (
        <section className={style.staffed}>
          {/* A real popover rather than a title attribute: the native one needs
              a second of hover, never appears on touch, and is easy to miss
              entirely. The green dot reads as "online right now" without it, and
              that is not what the line claims. */}
          <span className={style.here}>
            <span className={style.here_dot} aria-hidden="true" />
            Someone is on this stand
            <Tip
              label="What this means"
              text={
                `A named person at ${company.name} answers questions on this booth — ` +
                `not a shared inbox and not a bot. ${host.name} has personally answered ` +
                `${host.answeredCount} ${host.answeredCount === 1 ? 'question' : 'questions'} here` +
                `${host.typicalReply ? `, typically ${host.typicalReply}` : ''}.`
              }
            />
          </span>

          {badges.length > 0 && (
            <ul className={style.badges}>
              {badges.map((b) => (
                <li className={style.badge} key={b.key}>
                  {b.label}
                  <Tip label={`About the ${b.label} badge`} text={b.detail} onDark />
                </li>
              ))}
            </ul>
          )}

          <div className={style.person}>
            <span
              className={style.avatar}
              style={{ background: colors.background, color: colors.ink }}
              aria-hidden="true"
            >
              {String(host.name).trim().slice(0, 1).toUpperCase()}
            </span>
            <span className={style.person_id}>
              <strong>{host.name}</strong>
              {host.role && <span className={style.person_role}>{host.role}</span>}
            </span>
          </div>

          {/* Evidence, not a badge. Every one of these is derived from questions
              this company has actually answered. */}
          <ul className={style.proof}>
            <li>
              Answered <strong>{host.answeredCount}</strong>{' '}
              {host.answeredCount === 1 ? 'question' : 'questions'} here
            </li>
            {host.lastAnsweredAt && (
              <li>
                Last reply <strong>{timeAgo(host.lastAnsweredAt)}</strong>
                <span className={style.proof_date}> · {formatDate(host.lastAnsweredAt)}</span>
              </li>
            )}
            {host.typicalReply && (
              <li>
                Usually replies <strong>{host.typicalReply}</strong>
              </li>
            )}
          </ul>

          {sent ? (
            <p className={style.sent} role="status">
              Sent to {String(host.name).split(' ')[0]}. It appears on the booth once answered.
            </p>
          ) : open ? (
            <form className={style.form} onSubmit={submit}>
              <textarea
                className={style.input}
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 500))}
                placeholder={`Ask ${String(host.name).split(' ')[0]} something`}
                rows={2}
                maxLength={500}
                aria-label={`Ask ${host.name} a question`}
              />
              <div className={style.form_foot}>
                <span className={style.hint}>Goes privately to them.</span>
                <button
                  type="submit"
                  className={style.send}
                  style={{ background: brand }}
                  disabled={busy || draft.trim().length < 5}
                  data-log-action="spotlight_ask_question"
                >
                  {busy ? 'Sending…' : 'Send'}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className={style.ask}
              style={{ color: brand, borderColor: brand }}
              onClick={() => setOpen(true)}
              data-log-action="spotlight_open_ask"
            >
              Ask {String(host.name).split(' ')[0]} a question
            </button>
          )}

          {error && <p className={style.error} role="alert">{error}</p>}
        </section>
      )}

      {quiz && (
        <section className={style.quiz}>
          <span className={style.quiz_flag} style={{ color: brand }}>
            Fit check · {quiz.questionCount} questions · 2 min
          </span>
          <Quiz quiz={quiz} brand={brand} />
        </section>
      )}
    </div>
  )
}

export default BoothDesk
